import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ActualizarSecretoInput, SecretoFila } from '@leadsphere/types';

import { ConfirmModal } from '../../components/ui/ConfirmModal.js';
import { Table, type TableColumn } from '../../components/ui/Table.js';
import { ToastStack } from '../../components/ui/Toast.js';
import { useApi } from '../../hooks/useApi.js';
import { useToasts } from '../../hooks/useToasts.js';
import { ApiError } from '../../lib/api.js';

// Fase 3b (ls-agents/PLAN_NEXUS_FASE3.md; decisiones.md 2026-09-29) — módulo
// Secretos, SOLO root (la ruta y el menú también lo restringen). Las filas
// salen solas de los Mercados de las tarjetas Amazon Seller/Vendor; aquí solo
// se "cablean" los dos secrets y Sync. Cada cambio se guarda al momento.

interface OpcionesGcp {
  comerciales: string[];
  ads: string[];
  sin_asignar: string[];
}

const PREFIJO_COMERCIAL: Record<string, string> = { 'Amazon Seller': 'seller-', 'Amazon Vendor': 'vendor-' };
const SIN_SECRET = '';

function opcionesPara(nombres: string[], actual: string | null): string[] {
  return actual && !nombres.includes(actual) ? [actual, ...nombres] : nombres;
}

export function SecretosPage() {
  const { get, patch, delete: del } = useApi();
  const queryClient = useQueryClient();
  const { toasts, showToast, dismiss } = useToasts();

  const [tab, setTab] = useState<'asignaciones' | 'sin_asignar'>('asignaciones');
  const [plataformaFiltro, setPlataformaFiltro] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('');
  const [soloSinSecret, setSoloSinSecret] = useState(false);
  const [soloHuerfanas, setSoloHuerfanas] = useState(false);
  const [eliminarPendiente, setEliminarPendiente] = useState<SecretoFila | null>(null);

  const { data: filas, isLoading } = useQuery({
    queryKey: ['secretos'],
    queryFn: () => get<SecretoFila[]>('/secretos'),
  });
  const { data: gcp, error: gcpError } = useQuery({
    queryKey: ['secretos', 'gcp'],
    queryFn: () => get<OpcionesGcp>('/secretos/gcp'),
  });

  const guardar = useMutation({
    mutationFn: ({ id, cambios }: { id: string; cambios: ActualizarSecretoInput }) => patch(`/secretos/${id}`, cambios),
    onSuccess: () => {
      showToast('success', 'Guardado.');
      void queryClient.invalidateQueries({ queryKey: ['secretos'] });
    },
    onError: (err: unknown) => {
      showToast('error', err instanceof ApiError ? err.message : 'No fue posible guardar.');
      void queryClient.invalidateQueries({ queryKey: ['secretos'] });
    },
  });

  const eliminar = useMutation({
    mutationFn: (id: string) => del(`/secretos/${id}`),
    onSuccess: () => {
      showToast('success', 'Fila huérfana eliminada.');
      void queryClient.invalidateQueries({ queryKey: ['secretos'] });
    },
    onError: (err: unknown) => showToast('error', err instanceof ApiError ? err.message : 'No fue posible eliminar.'),
  });

  const estados = useMemo(() => [...new Set((filas ?? []).map(f => f.cliente_estatus))].sort(), [filas]);

  const filtradas = useMemo(
    () =>
      (filas ?? []).filter(
        f =>
          (!plataformaFiltro || f.plataforma === plataformaFiltro) &&
          (!estadoFiltro || f.cliente_estatus === estadoFiltro) &&
          (!soloSinSecret || !f.secret_comercial || !f.secret_ads) &&
          (!soloHuerfanas || f.huerfana),
      ),
    [filas, plataformaFiltro, estadoFiltro, soloSinSecret, soloHuerfanas],
  );

  const cambiar = (f: SecretoFila, cambios: ActualizarSecretoInput) => guardar.mutate({ id: f.id, cambios });

  const columns: TableColumn<SecretoFila>[] = [
    {
      key: 'cliente',
      header: 'Cliente',
      render: f => (
        <div>
          <div style={{ fontWeight: 600 }}>{f.cliente_nombre}</div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 3 }}>
            {f.huerfana && (
              <span className="ms-chip" title="Su país o su plataforma Amazon ya no están en el formulario del cliente. Conserva sus secrets.">
                huérfana
              </span>
            )}
            {f.compartido_con.length > 0 && (
              <span className="ms-chip" title="Otro cliente usa el mismo secret. No es un error; revisa Sync para no mandarlo dos veces.">
                compartido con {f.compartido_con.join(', ')}
              </span>
            )}
          </div>
        </div>
      ),
    },
    { key: 'kams', header: 'KAM(s)', render: f => <span style={{ color: 'var(--muted)' }}>{f.kams.join(', ') || '—'}</span> },
    { key: 'pais', header: 'Región', align: 'center', render: f => <strong>{f.pais_iso2}</strong> },
    { key: 'plataforma', header: 'Plataforma', render: f => f.plataforma },
    { key: 'estado', header: 'Estado', render: f => <span style={{ color: 'var(--muted)' }}>{f.cliente_estatus}</span> },
    {
      key: 'comercial',
      header: 'Secreto Comercial',
      render: f => {
        const prefijo = PREFIJO_COMERCIAL[f.plataforma] ?? '__ninguno__';
        const opciones = opcionesPara((gcp?.comerciales ?? []).filter(n => n.startsWith(prefijo)), f.secret_comercial);
        return (
          <select
            className="filter-input"
            value={f.secret_comercial ?? SIN_SECRET}
            disabled={!gcp || guardar.isPending}
            onChange={e => cambiar(f, { secret_comercial: e.target.value || null })}
          >
            <option value={SIN_SECRET}>— sin secret —</option>
            {opciones.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        );
      },
    },
    {
      key: 'ads',
      header: 'Secreto Ads',
      render: f => (
        <select
          className="filter-input"
          value={f.secret_ads ?? SIN_SECRET}
          disabled={!gcp || guardar.isPending}
          onChange={e => cambiar(f, { secret_ads: e.target.value || null })}
        >
          <option value={SIN_SECRET}>— sin secret —</option>
          {opcionesPara(gcp?.ads ?? [], f.secret_ads).map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      ),
    },
    {
      key: 'slug',
      header: 'Client slug',
      render: f => <SlugInput fila={f} disabled={guardar.isPending || f.sync} onGuardar={v => cambiar(f, { client_slug: v })} />,
    },
    {
      key: 'sync',
      header: 'Sync',
      align: 'center',
      render: f => (
        <label className="tog" title="Solo las filas marcadas, y con client slug, viajan a Onboarding">
          <input type="checkbox" checked={f.sync} disabled={guardar.isPending} onChange={e => cambiar(f, { sync: e.target.checked })} />
          <span className="tog-slider" />
        </label>
      ),
    },
    {
      key: 'acciones',
      header: '',
      render: f =>
        f.huerfana ? (
          <button className="cat-action-btn del" title="Eliminar fila huérfana" onClick={() => setEliminarPendiente(f)}>
            🗑️
          </button>
        ) : null,
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 19, fontWeight: 700, color: 'var(--heading)' }}>Secretos</div>
        <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 3 }}>
          Una fila por cliente × plataforma Amazon × mercado, generada desde las tarjetas de Clientes. Solo root.
        </div>
      </div>

      {gcpError && (
        <div className="ms-chip" style={{ marginBottom: 12 }}>
          No se pudo leer Secret Manager: {gcpError instanceof ApiError ? gcpError.message : 'error'} — los dropdowns quedan deshabilitados.
        </div>
      )}

      <div className="filters-bar">
        <button className={tab === 'asignaciones' ? 'btn-primary' : 'btn-ghost'} onClick={() => setTab('asignaciones')}>
          Asignaciones
        </button>
        <button className={tab === 'sin_asignar' ? 'btn-primary' : 'btn-ghost'} onClick={() => setTab('sin_asignar')}>
          Secrets sin asignar{gcp ? ` (${gcp.sin_asignar.length})` : ''}
        </button>
        {tab === 'asignaciones' && (
          <>
            <select className="filter-input" value={plataformaFiltro} onChange={e => setPlataformaFiltro(e.target.value)}>
              <option value="">Plataforma: todas</option>
              <option value="Amazon Seller">Amazon Seller</option>
              <option value="Amazon Vendor">Amazon Vendor</option>
            </select>
            <select className="filter-input" value={estadoFiltro} onChange={e => setEstadoFiltro(e.target.value)}>
              <option value="">Estado: todos</option>
              {estados.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
            <label style={{ fontSize: 12.5, display: 'flex', gap: 5, alignItems: 'center' }}>
              <input type="checkbox" checked={soloSinSecret} onChange={e => setSoloSinSecret(e.target.checked)} /> Solo sin secret
            </label>
            <label style={{ fontSize: 12.5, display: 'flex', gap: 5, alignItems: 'center' }}>
              <input type="checkbox" checked={soloHuerfanas} onChange={e => setSoloHuerfanas(e.target.checked)} /> Solo huérfanas
            </label>
          </>
        )}
      </div>

      {tab === 'asignaciones' ? (
        <Table
          columns={columns}
          data={filtradas}
          rowKey={f => f.id}
          emptyMessage={
            isLoading ? 'Cargando…' : 'Sin filas. Agrega Mercados a una tarjeta Amazon Seller/Vendor en Clientes para que aparezcan aquí.'
          }
          footer={<span className="table-count">{filtradas.length} fila{filtradas.length !== 1 ? 's' : ''}</span>}
        />
      ) : (
        <Table
          columns={[{ key: 'nombre', header: 'Secret en GCP sin asignar', render: (n: string) => <code>{n}</code> }]}
          data={gcp?.sin_asignar ?? []}
          rowKey={n => n}
          emptyMessage={gcp ? 'Todos los secrets de GCP están asignados.' : 'Cargando…'}
          footer={<span className="table-count">{gcp?.sin_asignar.length ?? 0} secret(s)</span>}
        />
      )}

      <ConfirmModal
        open={eliminarPendiente !== null}
        title="¿Eliminar esta fila huérfana?"
        body={
          eliminarPendiente
            ? `${eliminarPendiente.cliente_nombre} · ${eliminarPendiente.plataforma} ${eliminarPendiente.pais_iso2}. Se pierden sus secrets asignados (${[eliminarPendiente.secret_comercial, eliminarPendiente.secret_ads].filter(Boolean).join(', ') || 'ninguno'}). Los secrets en GCP no se tocan.`
            : ''
        }
        confirmLabel="Sí, eliminar"
        danger
        onConfirm={() => {
          if (eliminarPendiente) eliminar.mutate(eliminarPendiente.id);
          setEliminarPendiente(null);
        }}
        onCancel={() => setEliminarPendiente(null)}
      />
      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </div>
  );
}

// Fase 3c — se guarda al salir del campo o con Enter, no en cada tecla. Vacío = sin slug.
function SlugInput({ fila, disabled, onGuardar }: {
  fila: SecretoFila; disabled: boolean; onGuardar: (valor: string | null) => void;
}) {
  const [valor, setValor] = useState(fila.client_slug ?? '');
  useEffect(() => setValor(fila.client_slug ?? ''), [fila.client_slug]);
  const guardarSiCambio = () => {
    const limpio = valor.trim() || null;
    if (limpio !== fila.client_slug) onGuardar(limpio);
  };
  return (
    <input
      className="filter-input"
      style={{ width: 140 }}
      value={valor}
      disabled={disabled}
      placeholder="p. ej. rancho_mx"
      title={fila.sync ? 'Apaga Sync para cambiar el slug' : 'Exactamente el client_slug de Onboarding (llave del histórico en BigQuery)'}
      onChange={e => setValor(e.target.value)}
      onBlur={guardarSiCambio}
      onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
    />
  );
}
